<template>
  <div v-if="isFromNewRes">
    We found existing reservation(s) under your email/phone. You can modify your current reservation instead of creating a new one. <br />
    <p class="options">
      <span class="option">Option 1:</span>
      <span class="modify-res">Modify Existing Reservation (Recommended) <br />  
        Click Modify on the reservation below. Your existing reservation will be modified as per the selected details on the previous page.
      </span> <br />
    </p>
    <p class="options">
      <span class="option">Option 2:</span> 
      <span class="modify-res">Cancel an Existing Reservation <br />
        Click 'Cancel reservation' for the reservation(s) you don't plan to use. <br />
      </span>
    </p>
    <p class="options">
      <span class="option">Option 3:</span>
      <span class="modify-res">Create a New Reservation Anyway (Allowed) <br />
        You can still create an additional reservation by clicking 'Create new reservation'. Please remember to cancel any reservations you don’t plan to use. 
      </span>
    </p>
  </div>
  <div class="reservation-list">
    <div v-for="(res, k) in reservationList" :key="`res-${k}`">
      <!-- user details-->
      <div class="user">
        <div>
          <div>Name</div>
          <div v-if="res.isBusinessCustomer">
            <span class="notranslate">{{ isFromNewRes ? res.organizationName : maskName(res.organizationName) }}</span>
          </div>
          <div v-else>
            <span class="notranslate">{{ isFromNewRes ? res.firstName : maskName(res.firstName) }} 
              {{ isFromNewRes ? res.lastName : maskName(res.lastName) }}
            </span>
          </div>
        </div>
        <div v-if="!res.isBusinessCustomer">
          <div>DOB</div>
          <div>
            <span class="notranslate">
              {{ isFromNewRes ? getPrettyDob(res.dateOfBirth) : getHiddenValue(getPrettyDob(res.dateOfBirth)) }}
            </span>
          </div>
        </div>
        <div v-if="res.email.length > 0">
          <div>Email</div>
          <div><span class="notranslate">{{ isFromNewRes ? res.email : maskEmail(res.email) }}</span></div>
        </div>
        <div v-if="res.phone.length > 0">
          <div>Phone</div>
          <div><span class="notranslate">{{ isFromNewRes ? res.phone : maskPhone(res.phone) }}</span></div>
        </div>
      </div>
      <div class="service">
        <div class="details">
          <div class="item">
            <img class="app-icon" src="@ridmv/web-themes/images/icons/clipboard-list-solid.svg" alt="">
            <div>
              <div>Service(s)</div>
              <div class="desc">{{ res.visitTypeDescription }}</div>
            </div>
          </div>
          <div class="item">
            <img class="app-icon" src="@ridmv/web-themes/images/icons/map-marked-alt-solid.svg" alt="">
            <div>
              <div>Location</div>
              <div class="desc">
                <div>{{ res.reservationLocation.location }}</div>
                <div>{{ res.reservationLocation.addressLine1 }}</div>
                <div v-if="res.reservationLocation.addressLine2">{{ res.reservationLocation.addressLine2 }}</div>
                <div>
                  {{ res.reservationLocation.city }}, {{ res.reservationLocation.state }}
                  {{ res.reservationLocation.zip }}<span v-if="res.reservationLocation.zipPlus">-{{ res.reservationLocation.zipPlus }}</span>
                </div>
              </div>
            </div>
          </div>
          <div class="item">
            <img class="app-icon" src="@ridmv/web-themes/images/icons/calendar-solid.svg" alt="">
            <div>
              <div>Date and time</div>
              <div class="desc">
                <div>{{ formatWeekday(res.scheduleDate) }}</div>
                <div>{{ formatMonthDayYear(res.scheduleDate) }}</div>
                <div>{{ formatTime(res.startTime) }}</div>
              </div>
            </div>
          </div>
          <div class="item">
            <img class="app-icon" src="@ridmv/web-themes/images/icons/check-square-solid.svg" alt="">
            <div>
              <div>Confirmation number</div>
              <div class="desc">{{ res.appointmentNumber }}</div>
            </div>
          </div>
          <div class="item">
            <img class="app-icon" src="@ridmv/web-themes/images/icons/check-circle-solid.svg" alt="">
            <div>
              <div>Status</div>
              <div class="desc"
                   :class="{ 'past-appointment': (res.appointmentStatusDescription).toUpperCase() !== modifiableStatusName }">
                {{ res.appointmentStatusDescription }}
              </div>
            </div>
          </div>
        </div>

        <div class="actions" v-if="(res.appointmentStatusDescription).toUpperCase() === modifiableStatusName">
          <button :aria-label="`Modify reservation ${res.appointmentNumber}`" @click="modifyRes(res)">{{ language.btnModifyRes }}</button>
          <button :aria-label="`Cancel reservation ${res.appointmentNumber}`" :ref="(el) => cancelRefs[k] = el" @click="confirmDelete(res, k)">{{ language.btnCancelRes }}</button>
        </div>
      </div>
    </div>
    <ModalCancel :show-modal="showCancelModal" :code="cancelCode"
                 @doClose="showCancelModal = false" @doDeleteReservation="deleteRes" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import _ from 'underscore'; 
import { zonedTimeToUtc } from 'date-fns-tz';
import intervalToDuration from 'date-fns/intervalToDuration';
import ModalCancel from '@/components/reservation/modification/ModalCancel';
import { RESERVATION_FIND, RESERVATION_DELETE, SLOTS_HOLD_DELETE } from '@/store/_commons';
import {formatWeekday, formatMonthDayYear, formatTime} from '../../../utils/globalFilters';
import { confirmationMixin } from '@/utils/confirmationMixin';

// Component's properties 
const props = defineProps({
  listFound: {
    type: Array,
    required: false,
    default: () => [],
  },
  releaseSlotBeforeModify: {
    type: Boolean,
    default: false,
  },
  isFromNewRes: {
    type: Boolean,
    default: false,
  },
});

// Emitted events 
const emit = defineEmits(['doDeleteSuccess', 'doModifyNotAllowed', 'doModifyExisting']);

//Vue router
const router = useRouter();

// Vuex store 
const store = useStore();

// Composables for phone
const { getPrettyDob, getHiddenValue } = confirmationMixin();

// Reactive state variables 
const cancelCode = ref('');
const modifiableStatusName = 'SCHEDULED';
const showCancelModal = ref(false);
const reservationList = ref([]);
const modalCaller = ref('');
const cancelRefs = ref([]); 
const serviceRequirementValid = ref(true);

// Computed properties 
const language = computed(() => store.state.user.LANG);
const resLookup = computed(() => store.state.user.resLookup);
const resSelectedForUpdate = computed(() => store.state.user.resSelectedForUpdate);

// Watcher 
watch(showCancelModal, (newVal) => {
  if (!newVal && modalCaller.value !== '') {
    nextTick(() => {
      // Find the element with the matching ref from the array
      const callerButton = cancelRefs.value.find(
        (btn) => btn && btn.ariaLabel === `Cancel reservation ${modalCaller.value}`
      );
      if (callerButton) {
        callerButton.focus();
      }
    });
  }
});
// Lifecycle Hook
onMounted(() => {
  if (props.listFound.length === 0) {
    if (_.isEmpty(resLookup.value)) {
      router.push({ name: 'Lookup' });
    } else {
      reQueryRes();
    }
  } else {
    reservationList.value = props.listFound;
  }
});

// Methods
const deleteReservation = (payload) => store.dispatch(RESERVATION_DELETE, payload);
const findReservation = (payload) => store.dispatch(RESERVATION_FIND, payload);
const sendConfirmation = (payload) => store.dispatch('user/sendConfirmation', payload);
const setReservationDetails = (payload) => store.dispatch('user/setReservationDetails', payload);
const setStoreProperty = (payload) => store.dispatch('user/setStoreProperty', payload);

const confirmDelete = (res) => {
  modalCaller.value = `cxl${res.appointmentNumber}`;
  setReservationForUpdate(res);
  cancelCode.value = res.appointmentNumber;
  showCancelModal.value = false;
  nextTick(() => {
    showCancelModal.value = true;
  });
};

const deleteRes = async () => {
  showCancelModal.value = false;
  const delStatus = await deleteReservation(resSelectedForUpdate.value);
  await showDeleteFeedback(delStatus);
};

const getRimsDatetime = (res) => {
  const resDate = res.scheduleDate;
  const resTime = res.startTime;
  return resDate.replace('00:00:00', resTime);
};

const isModifyAllowed = (res) => {
  if (!res.isFeeApplicable) {
    return true;
  }
  const clientDatetime = new Date();
  const clientTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const clientDatetimeInUtc = zonedTimeToUtc(clientDatetime, clientTimezone);
  const TZ_ORIGIN = 'America/New_York';
  const rimsDatetime = getRimsDatetime(res);
  const rimsDatetimeInUtc = zonedTimeToUtc(rimsDatetime, TZ_ORIGIN);
  const duration = intervalToDuration({
    start: clientDatetimeInUtc,
    end: rimsDatetimeInUtc
  });
  console.warn('duration until res...', duration);
  return (duration.days >= 2);
};

const modifyRes = async (res) => {
  if (props.isFromNewRes) {
    emit('doModifyExisting', res);
    return;
  }
  serviceRequirementValid.value = isModifyAllowed(res);
  if (serviceRequirementValid.value) {
    setStoreProperty({ property: 'resType', value: res.visitTypeId });
    setReservationForUpdate(res);
    if (res.visitTypeId == 35 || res.visitTypeId == 36 || res.visitTypeId == 37) {
      setStoreProperty({ property: 'modifyReservation', value: true });
    }
    if (props.releaseSlotBeforeModify) {
      await store.dispatch(SLOTS_HOLD_DELETE);
    }
    router.push({ name: 'SlotSelection' });
  } else {
    emit('doModifyNotAllowed', { name: res.visitTypeDescription, confirmation: res.appointmentNumber });
  }
};

const notifyUser = async () => {
  let hasNotificationError = false;
  if (resSelectedForUpdate.value.email.length > 0 || resSelectedForUpdate.value.phone.length > 0) {
    const data = {
      appointmentNumber: resSelectedForUpdate.value.appointmentNumber,
      isCancellationConfirmation: true,
      email: resSelectedForUpdate.value.email,
      phone: resSelectedForUpdate.value.phone,
    };

    const response = await sendConfirmation(data);
    if (response !== undefined) {
      hasNotificationError = !response.isEmailSentSuccessfully || !response.isSmsSentSuccessfully;
    }
  }
  return hasNotificationError;
};

const reQueryRes = async () => {
  reservationList.value = await findReservation(resLookup.value);
  if (reservationList.value && reservationList.value.length === 0) {
    await router.push({ name: 'Lookup' });
  } 
};

const setReservationForUpdate = (res) => {
  setStoreProperty({ property: 'resSelectedForUpdate', value: res });
  const time = new Date(`2021-03-31T${res.startTime}`).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', hour12: true });
  const userProps = {
    appointmentNumber: res.appointmentNumber,
    appointmentStatusDescription: res.appointmentStatusDescription,
    dateOfBirth: res.dateOfBirth,
    firstName: res.firstName,
    isBusinessCustomer: res.isBusinessCustomer,
    lastName: res.lastName,
    licenseNumber: res.licenseNumber,
    middleName: res.middleName,
    organizationName: res.organizationName,
    plateNumber: res.plateNumber,
  };
  setReservationDetails([
    { property: 'serviceLocation', value: res.reservationLocation },
    { property: 'serviceDate', value: res.scheduleDate },
    { property: 'serviceTime', value: time },
    { property: 'serviceType', value: res.visitTypeDescription },
    { property: 'internalServiceTime', value: res.startTime },
    { property: 'userInfo', value: { ...userProps, reservationSendConfirmationRequest: {
      email: res.email,
      isConsentProvided: res.isConsentProvided,
      phone: res.phone,
    }}},
  ]);
  setStoreProperty({ property: 'resFeeApplicable', value: res.isFeeApplicable });
  setStoreProperty({ property: 'resTypeForCustomer', value: {
    allowedCustomerTypeId: -1,
    isFeeApplicable: res.isFeeApplicable,
    visitTypeId: res.visitTypeId
  }});
};

const showDeleteFeedback = async (delStatus) => {
  if (delStatus === store.state.user.STATUSES.SUCCESS) {
    reservationList.value = reservationList.value.filter(
      r => r.appointmentNumber !== resSelectedForUpdate.value.appointmentNumber
    );
    if (props.isFromNewRes) {
      store.commit('user/setReservationsFound', reservationList.value); // keep Vuex list in sync so a remount doesn't resurrect it
    }
    const hasError = await notifyUser();
    emit('doDeleteSuccess', hasError, reservationList.value.length);
  }
};

const maskEmail = (str) => { 
  const indexOfAt = str.indexOf('@'); 
  if (indexOfAt <= 2) { 
    return str; 
  }
  return str[0] + '*'.repeat(indexOfAt - 2) + str.substring(indexOfAt - 1);
};
const maskName = (name, visibleCharsStart = 1, visibleCharsEnd = 1, maskChar = '*') => {
  if (name.length <= visibleCharsStart + visibleCharsEnd) {
    return name;
  }

  const start = name.slice(0, visibleCharsStart);
  const end = name.slice(-visibleCharsEnd);
  const maskedSection = maskChar.repeat(name.length - visibleCharsStart - visibleCharsEnd);
  return `${start}${maskedSection}${end}`;
};

const maskPhone = (num) => { 
  const phoneArr = num.split('-');

  const myPhoneAC = phoneArr[1]?.replaceAll(/[&\\#, +()$~%.'":*?<>{}]/g, '');
  const myPhoneF3 = phoneArr[2]?.replaceAll(/\d(?=\d)/g, "*");
  const myPhoneL4 = phoneArr[3]?.replaceAll(/\d(?=\d)/g, "*");

  return `1-(${myPhoneAC})-${myPhoneF3}-${myPhoneL4}`;
};
</script>

<style scoped lang="scss">
.options {
  display: flex;
  gap: 1em;
  .option {
    font-weight: bold;
    min-width: 75px;
    .modify-res{
      font-weight: 200;
    }
    .modify-res-opt{
      position: relative;
    }
  }
}

.reservation-list {
  margin-top: 0.5em;
  .user {
    display: flex;
    flex-wrap: wrap;
    > div {
      display: flex;
      flex-direction: column;
      margin: 1em 0 auto;
      padding-right: 2.5em;
      > div:first-child { // label
        @extend %font-sans-bold;
      }
    }
  }
  .service {
    background-color: var(--ri-darker-blue);
    margin: 1em 0;
    .details {
      display: flex;
      justify-content: flex-start;
      flex-wrap: wrap;
      color: white;
      padding: 2em;
      width: 100%;
      .item {
        display: flex;
        margin-bottom: 2em;
        padding-right: clamp(2em, 3vw, 5vw);
        img ~ div {
          margin-left: 1em;
        }
        .desc {
          @extend %font-sans-bold;
          font-size: 1.25rem;
          line-height: 1.5em;
          padding-top: 0.25em;
          padding-right: 2em;
          &.past-appointment {
            opacity: 0.5;
          }
        }
        &:first-child { // services
          @media only screen and (max-width: 768px) {
            width: 100%;
          }
        }
        &:nth-child(2), &:nth-child(3), &:nth-child(4)  {  // location, date and time, conf num
        }
      }
    }
    .actions {
      padding: 0 2em 1em 2em;
      button {
        margin-right: 1em;
        margin-bottom: 1em;
        min-width: unset;
        &:hover {
          @extend %hover-btn-light;
        }
      }
    }
  }
}
</style>
